import { useEffect, useState } from "react";

// A lender's Account Executive contacts on the results page (design doc
// LenderDocsProcessors/docs/plans/ppe-lender-ae-contacts.md, scope of
// 2026-11-04): read only, Zeitro's default contacts only. The web server
// answers for several pricing engine lender keys at once
// (web/go/src/ppe_lender_ae_contacts.go).
export const AE_CONTACTS_URL = "/los/priceengine/lenderaecontacts";

// lender key -> [{ first_name, last_name, role, phone, email }], for the
// page's lifetime: each lender is asked for once, not per rate, hover, term
// or re-quote. A failed request is not kept, so the next search asks again.
const known = new Map();
// lender key -> the request asking for it, so a results table mounted again
// while it is out (a re-quote swaps the table) waits for it instead of asking twice.
const asking = new Map();

export function resetLenderAeContactsCache() {
  known.clear();
  asking.clear();
}

export function fetchLenderAeContacts(keys, fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return Promise.reject(new Error("AE contacts: no fetch"));
  const token = sessionStorage.getItem("ZeitroA");
  return Promise.resolve().then(() => doFetch(`${AE_CONTACTS_URL}?lenders=${encodeURIComponent(keys.join(","))}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })).then((res) => {
    if (!res.ok) throw new Error(`AE contacts: ${res.status}`);
    return res.json();
  }).then((body) => (body && body.lenders) || {});
}

const pick = (keys) => Object.fromEntries(keys.filter((k) => known.has(k)).map((k) => [k, known.get(k)]));

// The AE contacts of `lenderKeys` (the lenders in the results): one request
// for the lenders not asked for yet, whenever new ones appear. Contacts
// arrive after the results and never hold them up; on an error the lenders
// just show no AE.
export function useLenderAeContacts(lenderKeys, fetchImpl) {
  const keys = Array.from(new Set(lenderKeys.filter(Boolean))).sort();
  const keyList = keys.join(",");
  const [contacts, setContacts] = useState(() => pick(keys));

  useEffect(() => {
    const wanted = keyList ? keyList.split(",") : [];
    let live = true;
    setContacts(pick(wanted));
    const missing = wanted.filter((k) => !known.has(k) && !asking.has(k));
    if (missing.length > 0) {
      const request = fetchLenderAeContacts(missing, fetchImpl)
        .then((byLender) => {
          missing.forEach((k) => known.set(k, Array.isArray(byLender[k]) ? byLender[k] : []));
        })
        .catch(() => {})
        .then(() => missing.forEach((k) => asking.delete(k)));
      missing.forEach((k) => asking.set(k, request));
    }
    const pending = Array.from(new Set(wanted.filter((k) => asking.has(k)).map((k) => asking.get(k))));
    if (pending.length > 0) {
      const knownBefore = wanted.filter((k) => known.has(k)).length;
      Promise.all(pending).then(() => {
        // Nothing new (the request failed): no re-render.
        if (live && wanted.filter((k) => known.has(k)).length > knownBefore) setContacts(pick(wanted));
      });
    }
    return () => { live = false; };
  }, [keyList]); // eslint-disable-line react-hooks/exhaustive-deps

  return contacts;
}
